import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  Divider,
  Card,
  CardContent,
  Chip,
  Stack
} from '@mui/material';
import { Business, People, PersonSearch, CheckCircle, Person } from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useFilters } from '@/contexts/FilterContext';
import { apiService } from '@/services/api';
import { KPICard } from '@/components/KPICard/KPICard';
import { ActionsHistoryChart } from '@/components/Charts/ActionsHistoryChart';
import { ActionsByTypeChart } from '@/components/Charts/ActionsByTypeChart';
import { UniqueUsersChart } from '@/components/Charts/UniqueUsersChart';
import { PeriodComparisonChart } from '@/components/Charts/PeriodComparisonChart';
import { logger } from '@/utils/logger';

interface DashboardStats {
  totalTenants: number;
  activeUsers: number;
  activeSessions: number;
  successRateToday: number;
  uniqueUsersToday: number;
}

interface ActionsHistoryData {
  date: string;
  total: number;
  successful: number;
  failed: number;
  successRate: number;
}

interface ActionsByTypeData {
  action: string;
  count: number;
  successRate: number;
}

interface UniqueUsersData {
  date: string;
  uniqueUsers: number;
}

interface PeriodComparisonData {
  current: { total: number; successful: number; failed: number; uniqueUsers: number };
  previous: { total: number; successful: number; failed: number; uniqueUsers: number };
  changes: {
    total: number;
    successful: number;
    failed: number;
    uniqueUsers: number;
    successRate: number;
  };
}

export const DashboardPage: React.FC = () => {
  const { admin } = useAuth();
  const { setRefreshCallback } = useRefresh();
  const { selectedProductIds } = useFilters();

  // Stats básicos
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dados dos gráficos
  const [actionsHistory, setActionsHistory] = useState<ActionsHistoryData[]>([]);
  const [actionsByType, setActionsByType] = useState<ActionsByTypeData[]>([]);
  const [uniqueUsersHistory, setUniqueUsersHistory] = useState<UniqueUsersData[]>([]);
  const [periodComparison, setPeriodComparison] = useState<PeriodComparisonData | null>(null);

  const [chartsLoading, setChartsLoading] = useState(true);
  const [chartsError, setChartsError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDashboardStats(selectedProductIds);
      if (response.data.success) {
        setStats(response.data.data);
      } else {
        setError('Erro ao carregar estatísticas');
      }
    } catch (err: any) {
      logger.error('Error loading dashboard stats:', err);
      setError(err.response?.data?.message || 'Erro ao carregar estatísticas do dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedProductIds]);

  const loadChartsData = useCallback(async () => {
    try {
      setChartsLoading(true);
      setChartsError(null);

      const [historyRes, typeRes, usersRes, comparisonRes] = await Promise.all([
        apiService.getActionsHistory(30, selectedProductIds),
        apiService.getActionsByType(10, selectedProductIds),
        apiService.getUniqueUsersHistory(30, selectedProductIds),
        apiService.getPeriodComparison(7, selectedProductIds)
      ]);

      if (historyRes.data.success) {
        setActionsHistory(historyRes.data.data);
      }
      if (typeRes.data.success) {
        setActionsByType(typeRes.data.data);
      }
      if (usersRes.data.success) {
        setUniqueUsersHistory(usersRes.data.data);
      }
      if (comparisonRes.data.success) {
        setPeriodComparison(comparisonRes.data.data);
      }
    } catch (err: any) {
      logger.error('Error loading charts data:', err);
      setChartsError('Erro ao carregar dados dos gráficos');
    } finally {
      setChartsLoading(false);
    }
  }, [selectedProductIds]);

  const loadAllData = useCallback(async () => {
    await Promise.all([loadStats(), loadChartsData()]);
  }, [loadStats, loadChartsData]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Registrar callback de refresh
  useEffect(() => {
    setRefreshCallback(loadAllData);
    return () => setRefreshCallback(null);
  }, [loadAllData, setRefreshCallback]);

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'action.hover' }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Dashboard de Observabilidade
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Bem-vindo, {admin?.name}! Acompanhe a saúde operacional, atividade e uso do control
                plane no escopo de produtos selecionado.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color="primary"
                label={
                  selectedProductIds.length
                    ? `${selectedProductIds.length} produto(s) selecionado(s)`
                    : 'Todos os produtos permitidos'
                }
              />
              <Chip size="small" variant="outlined" label="Fonte: audit_logs + control plane" />
              <Chip size="small" variant="outlined" label="Janelas: hoje, 7 dias e 30 dias" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Seção 1: KPIs Principais */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          📊 Visão Geral
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Métricas principais do sistema para monitoramento em tempo real
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(5, 1fr)'
            },
            gap: 3
          }}
        >
          <KPICard
            title="Total de Tenants"
            value={stats?.totalTenants ?? 0}
            loading={loading}
            icon={<Business />}
            color="primary"
          />
          <KPICard
            title="Contas de Usuários Ativas"
            value={stats?.activeUsers ?? 0}
            subtitle="Total de contas não desativadas"
            loading={loading}
            icon={<People />}
            color="info"
          />
          <KPICard
            title="Sessões de Impersonação Ativas"
            value={stats?.activeSessions ?? 0}
            subtitle="Apenas sessões de impersonação"
            loading={loading}
            icon={<PersonSearch />}
            color="warning"
          />
          <KPICard
            title="Taxa de Sucesso Hoje"
            value={`${stats?.successRateToday ?? 0}%`}
            subtitle="Percentual de ações bem-sucedidas"
            loading={loading}
            icon={<CheckCircle />}
            color={stats && stats.successRateToday < 95 ? 'error' : 'success'}
          />
          <KPICard
            title="Usuários Únicos Hoje"
            value={stats?.uniqueUsersToday ?? 0}
            subtitle="Usuários distintos que realizaram ações hoje"
            loading={loading}
            icon={<Person />}
            color="primary"
          />
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Seção 2: Comparação de Períodos */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          📈 Comparação Semanal
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Compare os últimos 7 dias com o período anterior para identificar tendências e mudanças
          significativas. Use esta análise para detectar anomalias e planejar ações preventivas.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <PeriodComparisonChart
              data={
                periodComparison || {
                  current: { total: 0, successful: 0, failed: 0, uniqueUsers: 0 },
                  previous: { total: 0, successful: 0, failed: 0, uniqueUsers: 0 },
                  changes: { total: 0, successful: 0, failed: 0, uniqueUsers: 0, successRate: 0 }
                }
              }
              loading={chartsLoading}
              error={chartsError}
              periodLabel="7 dias"
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Seção 3: Tendências Temporais */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          📉 Tendências Temporais
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Acompanhe a evolução das ações, sucessos, falhas e taxa de sucesso ao longo dos últimos 30
          dias. Identifique padrões sazonais, picos de atividade e tendências de degradação antes
          que se tornem problemas críticos.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ActionsHistoryChart
              data={actionsHistory}
              loading={chartsLoading}
              error={chartsError}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Seção 4: Engajamento e Distribuição */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          👥 Engajamento e Distribuição
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Entenda como os usuários estão utilizando o sistema e quais tipos de ações são mais
          frequentes. Use estas informações para otimizar recursos e identificar áreas que precisam
          de atenção.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <UniqueUsersChart
              data={uniqueUsersHistory}
              loading={chartsLoading}
              error={chartsError}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ActionsByTypeChart data={actionsByType} loading={chartsLoading} error={chartsError} />
          </Grid>
        </Grid>
      </Box>

      {/* Seção 5: Insights e Alertas */}
      {stats && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            ⚠️ Alertas e Recomendações
          </Typography>
          <Grid container spacing={2}>
            {stats.successRateToday < 95 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="subtitle2" gutterBottom>
                    Taxa de Sucesso Abaixo do Ideal
                  </Typography>
                  <Typography variant="body2">
                    A taxa de sucesso hoje está em {stats.successRateToday}%, abaixo do ideal de
                    95%. Recomendamos investigar os logs de erro para identificar a causa raiz.
                  </Typography>
                </Alert>
              </Grid>
            )}
            {stats.uniqueUsersToday === 0 && stats.totalTenants > 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="subtitle2" gutterBottom>
                    Nenhum Usuário Ativo Hoje
                  </Typography>
                  <Typography variant="body2">
                    Não houve atividade de usuários hoje. Verifique se há problemas de conectividade
                    ou se é um comportamento esperado para este período.
                  </Typography>
                </Alert>
              </Grid>
            )}
            {periodComparison && periodComparison.changes.failed > 50 && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <Typography variant="subtitle2" gutterBottom>
                    Aumento Significativo de Falhas
                  </Typography>
                  <Typography variant="body2">
                    O número de falhas aumentou {periodComparison.changes.failed}% em relação ao
                    período anterior. Ação imediata recomendada para investigar e corrigir
                    problemas.
                  </Typography>
                </Alert>
              </Grid>
            )}
            {periodComparison && periodComparison.changes.successRate < -5 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="subtitle2" gutterBottom>
                    Degradação na Taxa de Sucesso
                  </Typography>
                  <Typography variant="body2">
                    A taxa de sucesso caiu {Math.abs(periodComparison.changes.successRate)}% em
                    relação ao período anterior. Monitore de perto e considere revisar as mudanças
                    recentes no sistema.
                  </Typography>
                </Alert>
              </Grid>
            )}
            {stats.successRateToday >= 95 && stats.uniqueUsersToday > 0 && (
              <Grid item xs={12}>
                <Alert severity="success">
                  <Typography variant="subtitle2" gutterBottom>
                    Sistema Operando Normalmente
                  </Typography>
                  <Typography variant="body2">
                    Todas as métricas estão dentro dos parâmetros esperados. O sistema está saudável
                    e operacional.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default DashboardPage;
